import uniqueId from 'lodash/uniqueId';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useMediaQueries } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import getArrayAsString from '../../../utils/getArrayAsString';
import handleGtmEventsForAccessories from '../utils/handleGtmEventsForAccessories';
import useBapLoadBuild from '../../ProgressBar/hooks/useBapLoadBuild';
import useBapRegisterUserInteraction from '../../../utils/hooks/bap/useBapRegisterUserInteraction';
import { addConfigureBapToastNotification, addGlobalToastNotification } from '../../../core/reducers/ui';
import { clearAccessoryAddQueue, clearAccessoryRemoveQueue, selectCurrentVehicle, selectCurrentVehicleAccessoryKeysAddQueue, selectCurrentVehicleAccessoryKeysRemoveQueue, toggleAccessory, } from '../../../core/reducers/currentVehicle';
import { gtmAccessoryNoOverlay } from '../../../core/gtm/gtmAccessorySelect';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../../core/reducers/vehicles';
import { useAppSelector } from '../../../core/utils';
const useAccessoryAddRemove = (selectedAccessoryKeys, availableAccessories) => {
    var _a;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isDesktop } = useMediaQueries();
    const { registerUserInteraction } = useBapRegisterUserInteraction('config');
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemsToAdd, setItemsToAdd] = useState([]);
    const [itemsToRemove, setItemsToRemove] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const vehicleInformation = useAppSelector(selectCurrentVehicle);
    const addQueue = useAppSelector(selectCurrentVehicleAccessoryKeysAddQueue);
    const removeQueue = useAppSelector(selectCurrentVehicleAccessoryKeysRemoveQueue);
    const { isUserLoggedIn, isBuildSaved, userIsBuildOwner } = useBapLoadBuild();
    const isBuildUpdatable = isUserLoggedIn && isBuildSaved && userIsBuildOwner;
    const getAccessoryByBusinessKey = (businessKey) => availableAccessories === null || availableAccessories === void 0 ? void 0 : availableAccessories.find(a => a.businessKey === businessKey);
    const getAccessoryById = (id) => availableAccessories === null || availableAccessories === void 0 ? void 0 : availableAccessories.find(a => a.id === id);
    const isAccessorySelected = (businessKey) => selectedAccessoryKeys.includes(businessKey);
    const addToastNotificationAction = !isDesktop ? addGlobalToastNotification : addConfigureBapToastNotification;
    const showToastNotificationForAccessoryChange = (businessKey) => {
        const generatedId = +uniqueId();
        const item = getAccessoryByBusinessKey(businessKey);
        const isSelected = isAccessorySelected(businessKey);
        if ((item === null || item === void 0 ? void 0 : item.hasOverlayAssets) && isSelected) {
            const message = isBuildUpdatable ? 'Pages.Bap.removingBuildToastText' : 'Pages.Bap.removeAccessoryToastText';
            dispatch(addToastNotificationAction({
                id: generatedId,
                message: t(message, {
                    accessoryName: item === null || item === void 0 ? void 0 : item.name,
                }),
            }));
        }
        else if ((item === null || item === void 0 ? void 0 : item.hasOverlayAssets) && !isSelected) {
            const message = isBuildUpdatable ? 'Pages.Bap.addingBuildToastText' : 'Pages.Bap.addAccessoryToastText';
            dispatch(addToastNotificationAction({
                id: generatedId,
                message: t(message, {
                    accessoryName: item === null || item === void 0 ? void 0 : item.name,
                }),
            }));
        }
        else if (!(item === null || item === void 0 ? void 0 : item.hasOverlayAssets) && !isSelected) {
            const message = isBuildUpdatable ? 'Pages.Bap.noOverlayBuildToastText' : 'Pages.Bap.noOverlayToastText';
            dispatch(addToastNotificationAction({
                id: generatedId,
                message: t(message, {
                    accessoryName: item === null || item === void 0 ? void 0 : item.name,
                }),
                hideIcon: true,
            }));
            gtmAccessoryNoOverlay({
                accessoriesSelected: item === null || item === void 0 ? void 0 : item.accessoryGtmName,
                gtmBodyStyle,
                trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
                model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
                availabilityFlag: vehicleInformation === null || vehicleInformation === void 0 ? void 0 : vehicleInformation.inventoryStatus,
            });
        }
    };
    const reset = () => {
        setShowModal(false);
        setItemsToAdd([]);
        setItemsToRemove([]);
        setSelectedItem(null);
        setIsAdding(false);
    };
    const applyChanges = (businessKey) => {
        registerUserInteraction();
        dispatch(toggleAccessory(businessKey));
        showToastNotificationForAccessoryChange(businessKey);
        reset();
    };
    const handleAccessorySelected = (businessKey, dontTriggerGtmEvent) => {
        var _a, _b, _c, _d;
        if (!businessKey) {
            return;
        }
        setSelectedItem(businessKey);
        const selectedAccessory = getAccessoryByBusinessKey(businessKey);
        if (!selectedAccessory) {
            return;
        }
        const isAddOperation = !isAccessorySelected(businessKey);
        if (isAddOperation && !dontTriggerGtmEvent) {
            handleGtmEventsForAccessories({
                accessory: selectedAccessory,
                gtmBodyStyle,
                trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
                model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
                inventoryStatus: vehicleInformation === null || vehicleInformation === void 0 ? void 0 : vehicleInformation.inventoryStatus,
            });
        }
        if (isAddOperation) {
            setIsAdding(true);
            // Adding an accessory, check for dependencies & exclusions
            const newItemsToRemove = (_b = (_a = selectedAccessory === null || selectedAccessory === void 0 ? void 0 : selectedAccessory.exclusions) === null || _a === void 0 ? void 0 : _a.map(id => {
                var _a;
                const keyToRemove = (_a = getAccessoryById(id)) === null || _a === void 0 ? void 0 : _a.businessKey;
                if (keyToRemove && isAccessorySelected(keyToRemove)) {
                    return keyToRemove;
                }
                return null;
            })) === null || _b === void 0 ? void 0 : _b.filter(Boolean);
            const newItemsToAdd = (_d = (_c = selectedAccessory === null || selectedAccessory === void 0 ? void 0 : selectedAccessory.dependencies) === null || _c === void 0 ? void 0 : _c.map(id => {
                var _a;
                const keyToAdd = (_a = getAccessoryById(id)) === null || _a === void 0 ? void 0 : _a.businessKey;
                if (keyToAdd && !isAccessorySelected(keyToAdd)) {
                    return keyToAdd;
                }
                return null;
            })) === null || _d === void 0 ? void 0 : _d.filter(Boolean);
            if (!(newItemsToRemove === null || newItemsToRemove === void 0 ? void 0 : newItemsToRemove.length) && !(newItemsToAdd === null || newItemsToAdd === void 0 ? void 0 : newItemsToAdd.length)) {
                applyChanges(businessKey);
                return;
            }
            setItemsToRemove(Array.from(new Set(newItemsToRemove === null || newItemsToRemove === void 0 ? void 0 : newItemsToRemove.filter(item => item !== null))));
            setItemsToAdd(Array.from(new Set(newItemsToAdd === null || newItemsToAdd === void 0 ? void 0 : newItemsToAdd.filter(item => item !== null))));
            setShowModal(true);
        }
        else {
            // Removing accessory, check for dependencies
            const dependentAccessories = availableAccessories.filter(acc => { var _a; return ((_a = acc === null || acc === void 0 ? void 0 : acc.dependencies) === null || _a === void 0 ? void 0 : _a.includes(selectedAccessory.id)) && isAccessorySelected(acc.businessKey); });
            const dependentAccessoriesKeys = dependentAccessories.map(acc => acc.businessKey);
            if (!dependentAccessoriesKeys.some(key => selectedAccessoryKeys.includes(key)) ||
                !(dependentAccessoriesKeys === null || dependentAccessoriesKeys === void 0 ? void 0 : dependentAccessoriesKeys.length)) {
                applyChanges(businessKey);
                return;
            }
            setItemsToRemove(Array.from(new Set(dependentAccessoriesKeys)));
            setShowModal(true);
        }
    };
    useEffect(() => {
        if (addQueue) {
            handleAccessorySelected(addQueue, true);
            dispatch(clearAccessoryAddQueue());
        }
    }, [addQueue]);
    useEffect(() => {
        if (removeQueue) {
            handleAccessorySelected(removeQueue);
            dispatch(clearAccessoryRemoveQueue());
        }
    }, [removeQueue]);
    const handleModalConfirm = () => {
        itemsToAdd === null || itemsToAdd === void 0 ? void 0 : itemsToAdd.forEach(businessKey => {
            dispatch(toggleAccessory(businessKey));
            showToastNotificationForAccessoryChange(businessKey);
        });
        itemsToRemove === null || itemsToRemove === void 0 ? void 0 : itemsToRemove.forEach(businessKey => {
            dispatch(toggleAccessory(businessKey));
            showToastNotificationForAccessoryChange(businessKey);
        });
        if (selectedItem) {
            dispatch(toggleAccessory(selectedItem));
            showToastNotificationForAccessoryChange(selectedItem);
        }
        reset();
    };
    const handleModalCancel = () => {
        reset();
    };
    const hydratedSelectedItem = selectedItem ? getAccessoryByBusinessKey(selectedItem) : null;
    const hydratedItemsToAdd = itemsToAdd.map(getAccessoryByBusinessKey);
    const hydratedItemsToRemove = itemsToRemove.map(getAccessoryByBusinessKey);
    const hasItemsToAdd = hydratedItemsToAdd.length > 0;
    const hasItemsToRemove = hydratedItemsToRemove.length > 0;
    const selectedItemPrice = (_a = hydratedSelectedItem === null || hydratedSelectedItem === void 0 ? void 0 : hydratedSelectedItem.provincialPrice) !== null && _a !== void 0 ? _a : 0;
    const calculatePriceChange = () => {
        if (!selectedItem || !hydratedItemsToAdd || !hydratedItemsToRemove) {
            return 0;
        }
        if (isAdding && hasItemsToAdd) {
            return (hydratedItemsToAdd.reduce((acc, item) => {
                if (item && item.provincialPrice) {
                    return acc + item.provincialPrice;
                }
                return acc;
            }, 0) + selectedItemPrice);
        }
        if (isAdding && hasItemsToRemove) {
            return (selectedItemPrice -
                hydratedItemsToRemove.reduce((acc, item) => {
                    if (item && item.provincialPrice) {
                        return acc + item.provincialPrice;
                    }
                    return acc;
                }, 0));
        }
        if (!isAdding && hasItemsToRemove) {
            return (0 -
                selectedItemPrice -
                hydratedItemsToRemove.reduce((acc, item) => {
                    if (item && item.provincialPrice) {
                        return acc + item.provincialPrice;
                    }
                    return acc;
                }, 0));
        }
    };
    let summaryDescription = '';
    const accessoryName = hydratedSelectedItem === null || hydratedSelectedItem === void 0 ? void 0 : hydratedSelectedItem.name;
    const andLabel = t('Pages.Bap.andLabel');
    if (isAdding && hydratedItemsToAdd.length) {
        const conflictAccessoryItems = hydratedItemsToAdd.map(item => item === null || item === void 0 ? void 0 : item.name);
        summaryDescription = t('Pages.Bap.inclusionAddText', {
            accessoryName,
            conflictAccessoryName: getArrayAsString(conflictAccessoryItems, andLabel),
        });
    }
    else if (!isAdding && hydratedItemsToRemove.length) {
        const conflictAccessoryItems = hydratedItemsToRemove.map(item => item === null || item === void 0 ? void 0 : item.name);
        summaryDescription = t('Pages.Bap.inclusionRemoveText', {
            accessoryName,
            conflictAccessoryName: getArrayAsString(conflictAccessoryItems, andLabel),
        });
    }
    else if (isAdding && hydratedItemsToRemove.length) {
        const conflictAccessoryItems = hydratedItemsToRemove.map(item => item === null || item === void 0 ? void 0 : item.name);
        summaryDescription = t('Pages.Bap.accessoryExclusionText', {
            accessoryName,
            conflictAccessoryName: getArrayAsString(conflictAccessoryItems, andLabel),
        });
    }
    return {
        handleAccessorySelected,
        handleModalConfirm,
        handleModalCancel,
        selectedItem: hydratedSelectedItem,
        itemsToAdd: hydratedItemsToAdd,
        itemsToRemove: hydratedItemsToRemove,
        showModal,
        isAdding,
        priceChange: calculatePriceChange() || 0,
        summaryDescription,
    };
};
export default useAccessoryAddRemove;
